import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';

import { FEATURE_FLAG_URL_PARAM_MAP } from './featureFlagUrlParamMap';
import { useAppSelector } from 'redux-stores';
import type { FeatureFlag, FeatureFlags } from 'stores/FeatureStore';

type MobxStores = {
  featureStore: {
    featureFlags: FeatureFlags | undefined;
    adminFeatureFlags: FeatureFlags | undefined;
    getURLParams: () => Record<string, string>;
  };
  messengerStore: {
    currentOrganizationId: string | null;
  };
};

function resolveFlag(
  flagName: FeatureFlag,
  flags: FeatureFlags | undefined,
  urlParams: Record<string, string>
): boolean {
  const urlParamName = FEATURE_FLAG_URL_PARAM_MAP[flagName];
  // URL params can only force a flag ON ('true'); forcing OFF via URL is not supported.
  if (urlParamName && urlParams[urlParamName] === 'true') return true;
  return Boolean(flags?.[flagName]);
}

export function useAdminFeatureFlag<T extends FeatureFlag[]>(
  flagNames: T
): Record<T[number], boolean> {
  const { featureStore, messengerStore } = useContext(MobXProviderContext) as MobxStores;
  const selectedOrganizationId = useAppSelector((state) => state.admin.selectedOrganizationId);

  const { featureFlags, adminFeatureFlags, getURLParams } = featureStore;
  const { currentOrganizationId } = messengerStore;

  const isViewingForeignOrg =
    Boolean(selectedOrganizationId) && selectedOrganizationId !== currentOrganizationId;

  const flags = isViewingForeignOrg ? adminFeatureFlags : featureFlags;
  const urlParams = getURLParams();

  return Object.fromEntries(
    flagNames.map((flagName) => [flagName, resolveFlag(flagName, flags, urlParams)])
  ) as Record<T[number], boolean>;
}
